import { Navigate } from '@tanstack/react-router';
import type { PropsWithChildren } from 'react';
import { normalizeWorkspaceIdRouteParam, type WorkspaceIdRouteParamInput } from '@/services/router/workspace-id-param';

/** The two Analysis URL segments. `analysis` is the legacy spelling. */
export type AnalysisSegment = 'analysis' | 'reports';

/** The pages served under either segment. */
export type AnalysisPage = 'deliverability' | 'email-clients';

const PAGE_ROUTES = {
  analysis: {
    deliverability: '/workspaces/$workspaceId/analysis/deliverability',
    'email-clients': '/workspaces/$workspaceId/analysis/email-clients',
  },
  reports: {
    deliverability: '/workspaces/$workspaceId/reports/deliverability',
    'email-clients': '/workspaces/$workspaceId/reports/email-clients',
  },
} as const;

/** The segment the app's URLs use; `analysis` is the legacy spelling, kept live for bookmarks. */
export const CANONICAL_ANALYSIS_SEGMENT: AnalysisSegment = 'reports';

export function analysisPageRoute(segment: AnalysisSegment, page: AnalysisPage) {
  return PAGE_ROUTES[segment][page];
}

/** True when `pathname` is this page under either segment. */
export function isAnalysisPagePathname(pathname: string, page: AnalysisPage) {
  return pathname.includes(`/analysis/${page}`) || pathname.includes(`/reports/${page}`);
}

/**
 * Renders a Deliverability/Email clients page at the canonical segment, and forwards the
 * legacy segment to it.
 *
 * Sits above the plan guard and the `last` workspace resolver so a non-canonical URL is
 * corrected before either runs, and the `last` sentinel is forwarded untouched for the
 * canonical route to resolve.
 */
type AnalysisRouteViewProps = PropsWithChildren<{
  segment: AnalysisSegment;
  page: AnalysisPage;
  workspaceId: WorkspaceIdRouteParamInput;
}>;

export function AnalysisRouteView({ segment, page, workspaceId: workspaceIdParam, children }: AnalysisRouteViewProps) {
  const workspaceId = normalizeWorkspaceIdRouteParam(workspaceIdParam);
  if (segment !== CANONICAL_ANALYSIS_SEGMENT) {
    // `search` carries the shared date range (start_time/end_time) across the redirect.
    return (
      <Navigate to={analysisPageRoute(CANONICAL_ANALYSIS_SEGMENT, page)} params={{ workspaceId }} search replace />
    );
  }

  return children;
}
